import { useMemo } from 'react';

import axios, { Axios } from 'axios';

import { oimApiConfig } from '../../config';
import { useAuthorization } from '../../utils/Api';

import { ApiKeyType, AppModel } from './types';

export type UpdateAppParams = Partial<AppModel>;
export type CreateAppParams = Partial<Omit<AppModel, 'appId'>>;

export interface RegenerateApiKeyParams extends Pick<AppModel, 'appId'> {
  apiKeyType: ApiKeyType;
}

export interface AppSecrets {
  apiPrimaryKey: string;
  apiSecondaryKey: string;
}

export interface CreateAppResponse extends Partial<AppModel>, AppSecrets {}

export interface RegenerateApiKeyResponse {
  apiPrimaryKey?: string;
  apiSecondaryKey?: string;
}

class AppsService {
  constructor(private readonly http: Axios) {}

  async getApps(): Promise<AppModel[]> {
    const { data } = await this.http.get<AppModel[]>('/apps', {
      transformResponse: res => JSON.parse(res),
    });

    return data;
  }

  async getApp(appId: string): Promise<AppModel> {
    const { data } = await this.http.get<AppModel>(`/apps/${appId}`, {
      transformResponse: res => JSON.parse(res),
    });

    return data;
  }

  async updateApp({ appId, ...params }: UpdateAppParams): Promise<AppModel> {
    const { data } = await this.http.patch<AppModel>(`/apps/${appId}`, params);

    return data;
  }

  async deleteApp(appId: string): Promise<void> {
    return this.http.delete(`/apps/${appId}`);
  }

  async createApp(params: CreateAppParams): Promise<CreateAppResponse> {
    const { data } = await this.http.post<CreateAppResponse>('/apps', params);

    return data;
  }

  async regenerateApiKey({
    apiKeyType,
    appId,
  }: RegenerateApiKeyParams): Promise<RegenerateApiKeyResponse> {
    const { data } = await this.http.post<RegenerateApiKeyResponse>(
      `/apps/${appId}/regenerate-api-key`,
      { apiKeyType },
    );

    return data;
  }
}

export const useAppsService = () => {
  const http = axios.create({
    baseURL: oimApiConfig.apiBaseUrl,
  });

  useAuthorization(http);
  return useMemo(() => new AppsService(http), [http]);
};
